import "@dxos/node-std/globals"

// packages/common/codec-protobuf/src/buffer-patch.ts
var patchBufferCodec = (codec) => ({
  encode: (x) => Buffer.from(codec.encode(x)),
  decode: codec.decode.bind(codec)
});

// packages/common/codec-protobuf/src/precompiled-mapping/create-message-mapper.ts
import assert from "@dxos/node-std/assert";
import * as pb from "protobufjs";

// packages/common/codec-protobuf/src/precompiled-mapping/codegen.ts
var Ref = Symbol("Ref");
var ref = (value) => ({
  [Ref]: true,
  value
});
var isRef = (value) => value[Ref] === true;
var codegen = (name, args, gen, ctx = {}) => {
  const newCtx = {
    ...ctx
  };
  let nextAnnon = 1;
  let buf = "";
  gen((parts, ...args2) => {
    const preprocessArg = (arg) => {
      if (isRef(arg)) {
        const name2 = `anon${nextAnnon++}`;
        newCtx[name2] = arg.value;
        return name2;
      } else {
        return arg;
      }
    };
    buf += parts.map((s, i) => s + (i < args2.length ? preprocessArg(args2[i]) : "")).join("") + "\n";
  });
  const code = `return function ${name}(${args.join(", ")}) {
${buf}
}`;
  return Function(...Object.keys(newCtx), code)(...Object.values(newCtx));
};

// packages/common/codec-protobuf/src/precompiled-mapping/create-message-mapper.ts
var createMessageMapper = (type, substitutions) => createMessageMapperCached(type, substitutions, {}).map;
var createMessageMapperCached = (type, substitutions, cache) => {
  if (!cache[type.fullName]) {
    cache[type.fullName] = {};
    cache[type.fullName].map = codegen(`${type.name}$map`, [
      "obj",
      "extraArgs"
    ], (c) => {
      c`const res = {};`;
      for (const field of type.fieldsArray) {
        field.resolve();
        c`if(obj.${field.name} !== undefined && obj.${field.name} !== null) {`;
        {
          const genMapScalar = (value) => {
            const substitution = field.resolvedType && substitutions[field.resolvedType.fullName.slice(1)];
            if (substitution) {
              const context = {
                messageName: type.fullName.slice(1),
                fieldName: field.name
              };
              c`${ref(substitution)}(${value}, ${ref(context)}, ...extraArgs)`;
            } else if (field.resolvedType && field.resolvedType instanceof pb.Type) {
              const mapper = createMessageMapperCached(field.resolvedType, substitutions, cache);
              c`${ref(mapper)}.map(${value}, extraArgs)`;
            } else {
              c`${value}`;
            }
          };
          if (field.repeated) {
            c`res.${field.name} = obj.${field.name}.map(item => `;
            genMapScalar("item");
            c`);`;
          } else if (field.map) {
            assert(field instanceof pb.MapField);
            c`res.${field.name} = {};`;
            c`for(const key of Object.keys(obj.${field.name})) {`;
            {
              c`res.${field.name}[key] = `;
              genMapScalar(`obj.${field.name}[key]`);
              c`;`;
            }
            c`}`;
          } else {
            c`res.${field.name} = `;
            genMapScalar(`obj.${field.name}`);
            c`;`;
          }
        }
        c`}`;
        if (!field.getOption("proto3_optional") && !field.repeated && !field.map && !field.partOf) {
          c`else {`;
          {
            if (field.resolvedType instanceof pb.Type) {
              const mapper = createMessageMapperCached(field.resolvedType, substitutions, cache);
              c`res.${field.name} = ${ref(mapper)}.map({}, extraArgs);`;
            } else if (field.resolvedType instanceof pb.Enum) {
              `res.${field.name} = 0;`;
            } else {
              c`res.${field.name} = ${getDefaultValue(field.type)};`;
            }
          }
          c`}`;
        }
      }
      c`return res;`;
    });
  }
  return cache[type.fullName];
};
var getDefaultValue = (type) => {
  switch (type) {
    case "double":
    case "float":
    case "int32":
    case "sfixed32":
    case "uint32":
    case "sint32":
    case "fixed32":
      return "0";
    case "sint64":
    case "int64":
    case "uint64":
    case "fixed64":
    case "sfixed64":
      return '"0"';
    case "bool":
      return "false";
    case "string":
      return '""';
    case "bytes":
      return "new Uint8Array()";
    default:
      throw new Error(`Unknown type: ${type}`);
  }
};

// packages/common/codec-protobuf/src/codec.ts
var OBJECT_CONVERSION_OPTIONS = {
  // Represent long integers as strings.
  longs: String,
  // Will set empty repeated fields to [] instead of undefined.
  arrays: true
};
var ProtoCodec = class {
  constructor(_type, _mapping, _schema) {
    this._type = _type;
    this._mapping = _mapping;
    this._schema = _schema;
    this._encodeMapper = createMessageMapper(this._type, this._mapping.encode);
    this._decodeMapper = createMessageMapper(this._type, this._mapping.decode);
  }
  /**
  * Underlying protobuf.js type descriptor.
  */
  get protoType() {
    return this._type;
  }
  get substitutionMappings() {
    return this._mapping;
  }
  /**
  * Reference to the protobuf schema this codec was created from.
  */
  get schema() {
    return this._schema;
  }
  encode(value, options = {}) {
    const sub = this._encodeMapper(value, [
      this._schema,
      options
    ]);
    return this._type.encode(sub).finish();
  }
  decode(data, options = {}) {
    const obj = this._type.toObject(this._type.decode(data), OBJECT_CONVERSION_OPTIONS);
    return this._decodeMapper(obj, [
      this._schema,
      options
    ]);
  }
  encodeAsAny(value, options = {}) {
    return {
      "@type": "google.protobuf.Any",
      type_url: this._type.fullName.slice(1),
      value: this.encode(value, options)
    };
  }
  fromObject(obj) {
    return this._decodeMapper(this._type.fromObject(obj).toJSON(), [
      this._schema
    ]);
  }
  /**
  * Dynamically add new definitions to this codec. Mutates the underlying schema.
  */
  addJson(schema) {
    this._schema.addJson(schema);
  }
};

// packages/common/codec-protobuf/src/encoding.ts
import * as protobuf from "protobufjs";
var encodeProtobuf = (root) => root.toJSON();
var decodeProtobuf = (struct) => protobuf.Root.fromJSON(struct);

// packages/common/codec-protobuf/src/mapping.ts
import assert2 from "@dxos/node-std/assert";
import protobufjs from "protobufjs";
var createMappingDescriptors = (substitutions) => {
  const encode = {};
  const decode = {};
  for (const type of Object.keys(substitutions)) {
    encode[type] = substitutions[type].encode;
    decode[type] = substitutions[type].decode;
  }
  return {
    encode,
    decode
  };
};
var mapMessage = async (type, mapper, obj) => {
  const res = {};
  for (const field of type.fieldsArray) {
    if (!(field.name in obj)) {
      continue;
    }
    res[field.name] = await mapField(field, mapper, obj[field.name]);
  }
  return res;
};
var mapField = async (field, mapper, value) => {
  if (!field.required && (value === null || value === void 0)) {
    return value;
  } else if (field.repeated) {
    return await Promise.all(value.map((value2) => mapScalarField(field, mapper, value2)));
  } else if (field.map) {
    assert2(field instanceof protobufjs.MapField);
    return await asyncObjectMap((value2) => mapScalarField(field, mapper, value2), value);
  } else {
    return mapScalarField(field, mapper, value);
  }
};
var mapScalarField = async (field, mapper, value) => {
  var _a;
  if (!field.resolved) {
    field.resolve();
  }
  const typeName = (_a = field.resolvedType) == null ? void 0 : _a.fullName.slice(1);
  if (typeName) {
    return await mapper(value, typeName);
  }
  if (field.resolvedType && field.resolvedType instanceof protobufjs.Type) {
    return await mapMessage(field.resolvedType, mapper, value);
  }
  return value;
};
var asyncObjectMap = async (map, record) => {
  const res = {};
  await Promise.all(Object.entries(record).map(async ([key, value]) => {
    res[key] = await map(value, key);
  }));
  return res;
};

// packages/common/codec-protobuf/src/sanitizer.ts
import { Enum as Enum2, Type as Type2 } from "protobufjs";
var sanitize = (type, value, path, context) => {
  if (!value) {
    return;
  }
  for (const key of Object.keys(value)) {
    if (!type.fields[key]) {
      continue;
    }
    const field = type.fields[key];
    if (field.repeated) {
      continue;
    }
    field.resolve();
    if (!field.resolvedType) {
      continue;
    }
    if (field.resolvedType instanceof Type2) {
      sanitize(field.resolvedType, value[key], `${path}.${key}`, context);
    } else if (field.resolvedType instanceof Enum2) {
      value[key] = sanitizeEnum(field.resolvedType, value[key], `${path}.${key}`, context);
    }
  }
};
var sanitizeEnum = (type, value, path, context) => {
  if (type.valuesById[value]) {
    return value;
  }
  if (typeof value === "string") {
    const normalizedValue = value.toLowerCase();
    for (const [name, tag] of Object.entries(type.values)) {
      if (name.toLowerCase() === normalizedValue) {
        return tag;
      }
    }
  }
  context.errors.push(`Invalid enum value: value=${JSON.stringify(value)} enum=${type.fullName} path=${path}`);
  return value;
};

// packages/common/codec-protobuf/src/schema.ts
import merge from "lodash.merge";
import protobufjs2, { Root as Root2 } from "protobufjs";

// packages/common/codec-protobuf/src/service.ts
import assert4 from "@dxos/node-std/assert";
import { getAsyncValue } from "@dxos/util";

// packages/common/codec-protobuf/src/stream.ts
import debug from "debug";
import assert3 from "@dxos/node-std/assert";
import { Context } from "@dxos/context";
var log = debug("dxos:codec-protobuf:stream");
var Stream = class {
  constructor(producer) {
    this._isClosed = false;
    this._isReady = false;
    /**
    * Buffer messages before subscription. Set to null when buffer is no longer needed.
    */
    this._buffer = [];
    this._readyPromise = new Promise((resolve) => {
      this._resolveReadyPromise = resolve;
    });
    this._ctx = new Context({
      onError: (err) => {
        var _a, _b;
        if (this._isClosed) {
          return;
        }
        this._isClosed = true;
        this._closeError = err;
        (_a = this._producerCleanup) == null ? void 0 : _a.call(this, err);
        (_b = this._closeHandler) == null ? void 0 : _b.call(this, err);
        void this._ctx.dispose();
      }
    });
    this._ctx.onDispose(() => {
      this.close();
    });
    try {
      const producerCleanup = producer({
        ctx: this._ctx,
        ready: () => {
          this._markAsReady();
        },
        next: (msg) => {
          if (this._isClosed) {
            log("Stream is closed, dropping message.");
            return;
          }
          this._markAsReady();
          if (this._messageHandler) {
            try {
              this._messageHandler(msg);
            } catch (err) {
              throwUnhandledRejection(err);
            }
          } else {
            assert3(this._buffer);
            this._buffer.push(msg);
          }
        },
        close: (err) => {
          var _a, _b;
          if (this._isClosed) {
            return;
          }
          this._isClosed = true;
          this._closeError = err;
          (_a = this._producerCleanup) == null ? void 0 : _a.call(this, err);
          try {
            (_b = this._closeHandler) == null ? void 0 : _b.call(this, err);
          } catch (err1) {
            throwUnhandledRejection(err1);
          }
          void this._ctx.dispose();
        }
      });
      if (producerCleanup) {
        this._producerCleanup = producerCleanup;
      }
    } catch (err) {
      this._ctx.raise(err);
    }
  }
  /**
  * Consumes the entire stream to the end until it closes and returns a promise with the resulting items.
  */
  static consume(stream) {
    return new Promise((resolve) => {
      const items = [];
      stream.onReady(() => {
        items.push({
          ready: true
        });
      });
      stream.subscribe((data) => {
        items.push({
          data
        });
      }, (error) => {
        if (error) {
          items.push({
            closed: true,
            error
          });
        } else {
          items.push({
            closed: true
          });
        }
        resolve(items);
      });
    });
  }
  /**
  * Maps all data coming through the stream.
  */
  static map(source, map) {
    return new Stream(({ ready, next, close }) => {
      source.onReady(ready);
      source.subscribe((data) => next(map(data)), close);
      return () => source.close();
    });
  }
  /**
  * Converts Promise<Stream<T>> to Stream<T>.
  */
  static unwrapPromise(streamPromise) {
    if (streamPromise instanceof Stream) {
      return streamPromise;
    }
    return new Stream(({ ready, next, close }) => {
      streamPromise.then((stream) => {
        stream.onReady(ready);
        stream.subscribe(next, close);
      }, (err) => {
        close(err);
      });
      return () => {
        streamPromise.then(
          (stream) => stream.close(),
          // eslint-disable-next-line n/handle-callback-err
          (err) => {
          }
        );
      };
    });
  }
  _markAsReady() {
    var _a;
    if (!this._isReady) {
      this._isReady = true;
      (_a = this._readyHandler) == null ? void 0 : _a.call(this);
      this._resolveReadyPromise();
    }
  }
  subscribe(onMessage, onClose) {
    assert3(!this._messageHandler, "Stream is already subscribed to.");
    assert3(!this._closeHandler, "Stream is already subscribed to.");
    assert3(this._buffer);
    for (const message of this._buffer) {
      try {
        onMessage(message);
      } catch (err) {
        throwUnhandledRejection(err);
      }
    }
    this._buffer = null;
    if (this._isClosed) {
      onClose == null ? void 0 : onClose(this._closeError);
      return;
    }
    this._messageHandler = onMessage;
    this._closeHandler = onClose;
  }
  /**
  * Resolves when stream is ready.
  */
  // TODO(burdon): Gather all callbacks into single observer.
  waitUntilReady() {
    return this._readyPromise;
  }
  /**
  * Registers a callback to be called when stream is ready.
  */
  onReady(onReady) {
    assert3(!this._readyHandler, "Stream already has a handler for the ready event.");
    this._readyHandler = onReady;
    if (this._isReady) {
      onReady();
    }
  }
  /**
  * Close the stream and dispose of any resources.
  */
  // TODO(burdon): Make async.
  close() {
    var _a, _b;
    if (this._isClosed) {
      return;
    }
    this._isClosed = true;
    (_a = this._producerCleanup) == null ? void 0 : _a.call(this);
    (_b = this._closeHandler) == null ? void 0 : _b.call(this, void 0);
    void this._ctx.dispose();
    this._messageHandler = void 0;
    this._closeHandler = void 0;
    this._producerCleanup = void 0;
  }
};
var throwUnhandledRejection = (error) => {
  setTimeout(() => {
    throw error;
  });
};

// packages/common/codec-protobuf/src/service.ts
var ServiceDescriptor = class {
  // prettier-ignore
  constructor(_service, _schema) {
    this._service = _service;
    this._schema = _schema;
  }
  get serviceProto() {
    return this._service;
  }
  get name() {
    return this._service.fullName.slice(1);
  }
  createClient(backend, encodingOptions) {
    return new Service(backend, this._service, this._schema, encodingOptions);
  }
  createServer(handlers, encodingOptions) {
    return new ServiceHandler(this._service, this._schema, handlers, encodingOptions);
  }
};
var Service = class {
  // prettier-ignore
  constructor(backend, service, schema, encodingOptions) {
    for (const method of service.methodsArray) {
      method.resolve();
      assert4(method.resolvedRequestType);
      assert4(method.resolvedResponseType);
      assert4(!method.requestStream, "Streaming RPC requests are not supported.");
      const requestCodec = schema.tryGetCodecForType(method.resolvedRequestType.fullName);
      const responseCodec = schema.tryGetCodecForType(method.resolvedResponseType.fullName);
      const methodName = mapRpcMethodName(method.name);
      if (method.responseStream) {
        this[methodName] = (request) => {
          const encoded = requestCodec.encode(request, encodingOptions);
          const stream = backend.callStream(method.name, {
            value: encoded,
            type_url: method.resolvedRequestType.fullName
          });
          return Stream.map(stream, (data) => responseCodec.decode(data.value, encodingOptions));
        };
      } else {
        this[methodName] = async (request) => {
          const encoded = requestCodec.encode(request, encodingOptions);
          const response = await backend.call(method.name, {
            value: encoded,
            type_url: method.resolvedRequestType.fullName
          });
          return responseCodec.decode(response.value, encodingOptions);
        };
      }
      Object.defineProperty(this[methodName], "name", {
        value: methodName
      });
    }
  }
};
var ServiceHandler = class {
  constructor(_serviceDefinition, _schema, _serviceProvider, _encodingOptions) {
    this._serviceDefinition = _serviceDefinition;
    this._schema = _schema;
    this._serviceProvider = _serviceProvider;
    this._encodingOptions = _encodingOptions;
  }
  /**
  * Request/response method call.
  */
  async call(methodName, request) {
    const { method, requestCodec, responseCodec } = this._getMethodInfo(methodName);
    assert4(!method.requestStream, "Invalid RPC method call: request streaming mismatch.");
    assert4(!method.responseStream, `Invalid RPC method call: response streaming mismatch. ${methodName}`);
    const mappedMethodName = mapRpcMethodName(methodName);
    const handler = await this._getHandler(mappedMethodName);
    const requestDecoded = requestCodec.decode(request.value, this._encodingOptions);
    const response = await handler(requestDecoded);
    const responseEncoded = responseCodec.encode(response, this._encodingOptions);
    return {
      value: responseEncoded,
      type_url: method.resolvedResponseType.fullName
    };
  }
  /**
  * Streaming method call.
  */
  callStream(methodName, request) {
    const { method, requestCodec, responseCodec } = this._getMethodInfo(methodName);
    assert4(!method.requestStream, "Invalid RPC method call: request streaming mismatch.");
    assert4(method.responseStream, `Invalid RPC method call: response streaming mismatch., ${methodName}`);
    const mappedMethodName = mapRpcMethodName(methodName);
    const handlerPromise = this._getHandler(mappedMethodName);
    const requestDecoded = requestCodec.decode(request.value, this._encodingOptions);
    const responseStream = Stream.unwrapPromise(handlerPromise.then((handler) => handler(requestDecoded)));
    return Stream.map(responseStream, (data) => ({
      value: responseCodec.encode(data, this._encodingOptions),
      type_url: method.resolvedResponseType.fullName
    }));
  }
  async _getHandler(method) {
    const service = await getAsyncValue(this._serviceProvider);
    const handler = service[method];
    assert4(handler, `Handler is missing: ${method}`);
    return handler.bind(service);
  }
  _getMethodInfo(methodName) {
    const method = this._serviceDefinition.methods[methodName];
    assert4(!!method, `Method not found: ${methodName}`);
    method.resolve();
    assert4(method.resolvedRequestType);
    assert4(method.resolvedResponseType);
    const requestCodec = this._schema.tryGetCodecForType(method.resolvedRequestType.fullName);
    const responseCodec = this._schema.tryGetCodecForType(method.resolvedResponseType.fullName);
    return {
      method,
      requestCodec,
      responseCodec
    };
  }
};
var mapRpcMethodName = (name) => name[0].toLocaleLowerCase() + name.substring(1);

// packages/common/codec-protobuf/src/schema.ts
var Schema = class {
  static fromJson(schema, substitutions = {}) {
    const root = protobufjs2.Root.fromJSON(schema);
    return new Schema(root, substitutions);
  }
  constructor(_typesRoot, substitutions) {
    this._typesRoot = _typesRoot;
    this._codecCache = {};
    this._mapping = createMappingDescriptors(substitutions);
  }
  getCodecForType(typeName) {
    var _a, _b, _c;
    if (typeof typeName !== "string") {
      throw new TypeError("Expected `typeName` argument to be a string");
    }
    const type = this._typesRoot.lookupType(typeName);
    (_c = (_a = this._codecCache)[_b = type.fullName]) != null ? _c : _a[_b] = new ProtoCodec(type, this._mapping, this);
    return this._codecCache[type.fullName];
  }
  hasType(typeName) {
    if (typeName === "") {
      return false;
    }
    try {
      this._typesRoot.lookupType(typeName);
      return true;
    } catch (e) {
      return false;
    }
  }
  tryGetCodecForType(typeName) {
    var _a, _b, _c;
    if (typeName === "") {
      throw new Error(`Type not found: "${typeName}"`);
    }
    if (typeof typeName !== "string") {
      throw new TypeError("Expected `typeName` argument to be a string");
    }
    const type = this._typesRoot.lookupType(typeName);
    (_c = (_a = this._codecCache)[_b = type.fullName]) != null ? _c : _a[_b] = new ProtoCodec(type, this._mapping, this);
    return this._codecCache[type.fullName];
  }
  getService(name) {
    if (typeof name !== "string") {
      throw new TypeError("Expected `name` argument to be a string");
    }
    const service = this._typesRoot.lookupService(name);
    return new ServiceDescriptor(service, this);
  }
  /**
  * Dynamically add new definitions to this schema.
  */
  addJson(schema) {
    if (!schema.nested) {
      throw new Error("Invalid schema: missing nested object");
    }
    this._typesRoot = Root2.fromJSON(merge(this._typesRoot.toJSON(), schema));
  }
};

// packages/common/codec-protobuf/src/substitutions/struct.ts
var encodeStructValue = (structValue) => {
  const valueType = typeof structValue;
  switch (valueType) {
    case "undefined": {
      return {
        nullValue: 0
      };
    }
    case "number": {
      return {
        numberValue: structValue
      };
    }
    case "string": {
      return {
        stringValue: structValue
      };
    }
    case "boolean": {
      return {
        boolValue: structValue
      };
    }
    case "object": {
      if (structValue === null) {
        return {
          nullValue: 0
        };
      }
      if (Array.isArray(structValue)) {
        return {
          listValue: {
            values: structValue.map(encodeStructValue)
          }
        };
      }
      return {
        structValue: encodeStruct(structValue)
      };
    }
    default: {
      throw new Error(`Unsupported type: ${valueType}`);
    }
  }
};
var encodeStruct = (struct) => ({
  fields: Object.fromEntries(Object.entries(struct).map(([key, value]) => [
    key,
    encodeStructValue(value)
  ]))
});
var decodeStructValue = (structValue) => {
  const [key, v] = Object.entries(structValue)[0];
  switch (key) {
    case "nullValue": {
      return null;
    }
    case "numberValue": {
      return v;
    }
    case "stringValue": {
      return v;
    }
    case "boolValue": {
      return v;
    }
    case "structValue": {
      return decodeStruct(v);
    }
    case "listValue": {
      return v.values.map(decodeStructValue);
    }
    default:
      throw new Error(`Unsupported type: ${key}`);
  }
};
var decodeStruct = (struct) => Object.fromEntries(Object.entries(struct.fields || {}).map(([key, value]) => [
  key,
  decodeStructValue(value)
]));
var structSubstitutions = {
  "google.protobuf.Struct": {
    encode: (value) => encodeStruct(value),
    decode: (value) => decodeStruct(value)
  }
};

// packages/common/codec-protobuf/src/substitutions/any.ts
var anySubstitutions = {
  "google.protobuf.Any": {
    encode: (value, context, schema, options) => {
      const field = schema.getCodecForType(context.messageName).protoType.fields[context.fieldName];
      if (options.preserveAny || field.getOption("preserve_any")) {
        if (value["@type"] && value["@type"] !== "google.protobuf.Any") {
          throw new Error("Can only encode google.protobuf.Any with @type set to google.protobuf.Any in preserveAny mode.");
        }
        return value;
      }
      if (typeof value["@type"] !== "string") {
        throw new Error("Cannot encode google.protobuf.Any without @type string field");
      }
      if (value["@type"] === "google.protobuf.Any") {
        return value;
      }
      if (value["@type"] === "google.protobuf.Struct") {
        const codec = schema.tryGetCodecForType(value["@type"]);
        return codec.encodeAsAny(structSubstitutions["google.protobuf.Struct"].encode(value));
      }
      const codec1 = schema.tryGetCodecForType(value["@type"]);
      return codec1.encodeAsAny(value);
    },
    decode: (value, context, schema, options) => {
      var _a, _b;
      const field = schema.getCodecForType(context.messageName).protoType.fields[context.fieldName];
      if (options.preserveAny || field.getOption("preserve_any")) {
        return {
          "@type": "google.protobuf.Any",
          type_url: (_a = value.type_url) != null ? _a : "",
          value: (_b = value.value) != null ? _b : new Uint8Array()
        };
      }
      if (!schema.hasType(value.type_url)) {
        return {
          "@type": "google.protobuf.Any",
          ...value
        };
      }
      const codec = schema.tryGetCodecForType(value.type_url);
      let data = codec.decode(value.value);
      if (value.type_url === "google.protobuf.Struct") {
        data = structSubstitutions["google.protobuf.Struct"].decode(data);
      }
      return {
        ...data,
        "@type": value.type_url
      };
    }
  }
};

// packages/common/codec-protobuf/src/substitutions/timestamp.ts
var timestampSubstitutions = {
  "google.protobuf.Timestamp": {
    encode: (value) => {
      const unixMilliseconds = value.getTime();
      return {
        seconds: Math.floor(unixMilliseconds / 1e3).toString(),
        nanos: unixMilliseconds % 1e3 * 1e6
      };
    },
    decode: (value) => {
      var _a, _b;
      return new Date(parseInt((_a = value.seconds) != null ? _a : "0") * 1e3 + ((_b = value.nanos) != null ? _b : 0) / 1e6);
    }
  }
};

// packages/common/codec-protobuf/src/compression.ts
import { compress, decompress } from "compress-json";
var compressSchema = (data) => compress(data);
var decompressSchema = (data) => decompress(data);
export {
  OBJECT_CONVERSION_OPTIONS,
  ProtoCodec,
  Schema,
  Service,
  ServiceDescriptor,
  ServiceHandler,
  Stream,
  anySubstitutions,
  compressSchema,
  createMappingDescriptors,
  decodeProtobuf,
  decompressSchema,
  encodeProtobuf,
  mapMessage,
  patchBufferCodec,
  sanitize,
  structSubstitutions,
  timestampSubstitutions
};
//# sourceMappingURL=index.mjs.map
